import React, { useState } from "react";
import {
  PROCEEDS_OPTIONS,
  SECURITY_PURCHASES_OPTIONS,
  DIVIDEND_OPTIONS,
  DEFAULT_DIVIDEND_OPTION,
  REGISTRATION_OPTIONS,
  getAccountTypesForRegistration,
} from "../../../../constants/accountConstants";
import { isRetirementAccount } from "../../../../utils/formatters";

const isMissing = (val) => val === null || val === undefined || val === "";

const BasicDetailsForm = ({
  householdData, fieldData, missingFields, entityId, onSave, onSaveAndExit, saving, nextLabel,
}) => {
  const account = fieldData || {};
  const isIws = householdData?.is_fee_standard !== false;
  const aumAvailability = householdData?.aum_availability;
  const showAccountValue =
    !isIws &&
    (aumAvailability === null || aumAvailability === undefined || aumAvailability === 1 || aumAvailability === 2);

  const ALL_FIELDS = [
    { key: "custodian", label: "Custodian", type: "text" },
    { key: "registration", label: "Registration", type: "select", options: REGISTRATION_OPTIONS },
    { key: "type", label: "Type", type: "dependent_select", dependsOn: "registration", getOptions: getAccountTypesForRegistration },
    { key: "proceeds", label: "Proceeds", type: "select", options: PROCEEDS_OPTIONS },
    { key: "security_purchases", label: "Security Purchases", type: "select", options: SECURITY_PURCHASES_OPTIONS },
    { key: "distribution_income", label: "Dividend/Distribution Income", type: "select", options: DIVIDEND_OPTIONS },
    ...(!isRetirementAccount(account.registration) ? [{ key: "margin", label: "Are Margins enabled?", type: "boolean_select" }] : []),
    { key: "options", label: "Are Options enabled?", type: "boolean_select" },

    ...(showAccountValue ? [{ key: "account_value", label: "Account Value", type: "currency" }] : []),
  ];

  const fieldsToShow = ALL_FIELDS.filter((f) => {
    if (f.type === "boolean_select") return account[f.key] === null || account[f.key] === undefined;
    return isMissing(account[f.key]);
  });
  const missingCount = fieldsToShow.length;

  const [formData, setFormData] = useState(() => {
    const initial = {};
    fieldsToShow.forEach((f) => {
      if (f.key === "distribution_income") {
        initial[f.key] = DEFAULT_DIVIDEND_OPTION;
      } else {
        initial[f.key] = "";
      }
    });
    return initial;
  });

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "registration") {
        next.type = "";
      }
      return next;
    });
  };

  const buildPayload = () => {
    const update = {};
    fieldsToShow.forEach((f) => {
      if (f.type === "currency") {
        const val = parseFloat(formData[f.key]);
        if (!isNaN(val) && val >= 0) update[f.key] = val;
      } else if (f.type === "boolean_select") {
        if (formData[f.key] === "true") update[f.key] = true;
        else if (formData[f.key] === "false") update[f.key] = false;
      } else if (f.type === "select") {
        const val = formData[f.key];
        if (val) update[f.key] = val;
      } else {
        const val = formData[f.key]?.trim();
        if (val) update[f.key] = val;
      }
    });
    return { accounts: { [entityId]: update } };
  };

  const handleSubmit = (cb) => cb(buildPayload());

  if (missingCount === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <p className="text-sm">All basic details have been filled.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-gray-900">Missing Information</h2>
        <span className="text-sm text-red-500 font-medium">
          ● {missingCount} Required field{missingCount !== 1 ? "s" : ""} missing
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
        {fieldsToShow.map((f) => (
          <div key={f.key}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {f.label} <span className="text-red-500">*</span>
            </label>
            {f.type === "dependent_select" ? (
              <select
                value={formData[f.key]}
                onChange={(e) => handleChange(f.key, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">Select...</option>
                {f.getOptions(formData[f.dependsOn]).map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            ) : f.type === "currency" ? (
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <input type="number" min="0" step="0.01" value={formData[f.key]} onChange={(e) => handleChange(f.key, e.target.value)} onKeyDown={(e) => { if (e.key === "e" || e.key === "E") e.preventDefault(); }} className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
              </div>
            ) : f.type === "select" ? (
              <select
                value={formData[f.key]}
                onChange={(e) => handleChange(f.key, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">Select...</option>
                {f.options.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            ) : f.type === "boolean_select" ? (
              <select
                value={formData[f.key]}
                onChange={(e) => handleChange(f.key, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">Select...</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            ) : (
              <input type="text" value={formData[f.key]} onChange={(e) => handleChange(f.key, e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
        <button onClick={() => handleSubmit(onSaveAndExit)} disabled={saving} className="px-6 py-2.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50">Save & Exit</button>
        <button onClick={() => handleSubmit(onSave)} disabled={saving} className="px-6 py-2.5 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center gap-2">
          {saving && <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>}
          {nextLabel || "Save & Next →"}
        </button>
      </div>
    </div>
  );
};

export default BasicDetailsForm;
